import React from "react";
import { PauseCircleOutline, PlayArrow, ScheduleOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import { WorkflowPauseConfig, WorkflowPauseUnit } from "./workflowBuilderData";

interface WorkflowPauseStageProps {
  config: WorkflowPauseConfig;
  owner: string;
  onResume: () => void;
  /** A real case's own resume time (Eastern, as stored) — replaces the authored duration. */
  pausedUntil?: string;
  /** A resume is in flight. */
  busy?: boolean;
  banner?: React.ReactNode;
}

const UNIT_LABELS: Record<WorkflowPauseUnit, string> = {
  hours: "hours",
  days: "days",
  "working-days": "working days",
};

const WorkflowPauseStage: React.FC<WorkflowPauseStageProps> = ({
  config,
  owner,
  onResume,
  pausedUntil,
  busy = false,
  banner,
}) => {
  const amount = Math.max(1, config.amount);
  // An unset unit only reaches here from a draft Validate would have refused.
  const unit = config.unit ? UNIT_LABELS[config.unit] : "units";

  return (
    <>
      <Alert
        severity="info"
        icon={<PauseCircleOutline />}
        className="workflow-run__alert--flush"
      >
        {pausedUntil ? (
          <>
            The case is paused until <strong>{pausedUntil}</strong>, then continues automatically.
          </>
        ) : (
          <>
            The case is paused for{" "}
            <strong>
              {amount} {unit}
            </strong>
            , then continues automatically.
          </>
        )}
      </Alert>

      <Box className="workflow-run__verify">
        <span className="workflow-run__avatar workflow-run__avatar--wait" aria-hidden="true">
          <ScheduleOutlined />
        </span>
        <Box className="workflow-run__verify-copy">
          <Typography className="workflow-run__verify-name">Waiting…</Typography>
          <Typography className="workflow-run__verify-meta">
            {pausedUntil ? `Resumes ${pausedUntil}` : `${amount} ${unit} remaining`}
          </Typography>
        </Box>
      </Box>

      {banner}

      <Box className="workflow-run__actions">
        <Typography className="workflow-run__hint workflow-run__hint--flush">
          {owner ? `${owner} can resume early.` : "The canvas owner can resume early."}
        </Typography>
        <Box className="workflow-run__actions-spacer" />
        <Button
          className="workflow-run__dark-action"
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={onResume}
          disabled={busy}
        >
          Resume now
        </Button>
      </Box>
    </>
  );
};

export default WorkflowPauseStage;
